import fields from './fields';

/**
 * Whether a sheet column is drawn.
 *
 * One definition, because there are three places that decide it — the header,
 * the body, and the mobile card list — and they have to agree or a column
 * appears in one and not the others.
 *
 * TWO RULES:
 *
 *   1. `public` is what keeps internal columns off the sheet. It is the field's
 *      own declaration in fields.js and nothing overrides it, including the rule
 *      below: a non-public column stays off even when filtered, because there is
 *      no way to set such a filter from the UI in the first place.
 *   2. `hidden` is the user's own layout choice — theirs, or the curated default
 *      in defaultSheetLayout.js — AND IT IS OVERRIDDEN BY AN ACTIVE FILTER.
 *
 * WHY RULE 2 EXISTS. Filtering on a column nobody can see produces a sheet
 * showing nine of forty rows with nothing on screen explaining the other
 * thirty-one, which reads as data loss rather than a filter.
 *
 * It was always reachable — the header's right-click menu can filter a column
 * and then hide it — and it got easier to reach on 2026-09-13, when the
 * dashboard's stale-stock card started opening Inventory filtered on `Days`. A
 * user who has hidden `Days` (it is on the curated hide-list, taken from the
 * owner's own production layout) now arrives at a sheet withholding rows on a
 * criterion that is nowhere on screen. Note this is about the user's SAVED
 * preference rather than the curated default, which does not reach Inventory at
 * all — that sheet keeps its legacy raw-order behaviour and hides nothing by
 * default. SheetFilterChips is the other half of the answer; this is the half
 * that puts the number itself back in front of them.
 *
 * IT IS A RENDER-TIME RULE AND MUST STAY ONE. It deliberately does not write
 * through `persistFieldOrder`: revealing a column for as long as you are
 * filtering by it is not the same as editing the layout the user saved, and one
 * dashboard click should not rearrange the sheet they come back to tomorrow.
 * `fieldOrder` is untouched, so clearing the filter puts the column away again.
 */
export function isColumnVisible(fieldObj, filterObject) {
  if (!fields[fieldObj?.name]?.public) return false;
  if (!fieldObj.hidden) return true;
  return Boolean(filterObject?.[fieldObj.name]?.filterType);
}

/**
 * The filters currently doing something, as `[name, filter]` pairs.
 *
 * `filterDataUtility` clears a filter by writing `{}` rather than deleting the
 * key, so "has a key" is not the same as "is filtered" — the `filterType` check
 * is what tells a live filter from a cleared one. Both the chips and the
 * visibility rule above need that distinction, so it is written once.
 */
export function activeFilters(filterObject) {
  return Object.entries(filterObject || {}).filter(([, f]) => f && f.filterType);
}
